/**
 * FrontSpeakerGhostModel — renders a transparent ghost rectangle for front speaker(s).
 *
 * Rendering rules:
 * - previewMap !== null AND updating: show ALL features (merge preview + Redux)
 * - previewMap !== null AND NOT updating: show only the dragged feature (preview entries)
 * - previewMap === null: show all from Redux (worker update / creation)
 */
import {useMemo, useEffect, useRef, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {createRoundedRectShape} from '../../../utils/geometry'
import {useAppSelector} from '../../../store'
import {selectActiveScene} from '../../store/scenesSlice'
import {getPreviewFrontSpeakerSnapshot, subscribePreviewFrontSpeaker} from '../previewFrontSpeaker'
import {
  getRemovedFrontFeatureSnapshot,
  subscribeRemovedFrontFeature,
  clearRemovedFrontFeature,
} from '../removedFrontFeatureStore'
import type {RemovedFrontSpeakerGhost} from '../removedFrontFeatureStore'
import type {FrontSpeakerFeature} from '../../types'
import {FRONT_FACE_EXTRUDE} from '../../workerGeo/helpers'
import {GHOST_COLOR, REMOVED_COLOR, GHOST_OPACITY} from './ghostStyle'
const Z_OFFSET = 0.15

// ── Single front speaker ghost ───────────────────────────────────

interface SingleGhostProps {
  width: number
  height: number
  cornerRadius: number
  xOffset: number
  yOffset: number
  ghostZ: number
  color?: number
}

const SingleFrontSpeakerGhost = ({
  width,
  height,
  cornerRadius,
  xOffset,
  yOffset,
  ghostZ,
  color = GHOST_COLOR,
}: SingleGhostProps) => {
  const cr = Math.min(cornerRadius, width / 2, height / 2)

  const prevGeoRef = useRef<THREE.ShapeGeometry | null>(null)
  const geo = useMemo(() => {
    const shape = createRoundedRectShape(width, height, cr)
    return new THREE.ShapeGeometry(shape, 16)
  }, [width, height, cr])

  useEffect(() => {
    if (prevGeoRef.current && prevGeoRef.current !== geo) {
      prevGeoRef.current.dispose()
    }
    prevGeoRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  return (
    <mesh geometry={geo} position={[xOffset, yOffset, ghostZ]}>
      <meshBasicMaterial color={color} transparent opacity={GHOST_OPACITY} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  )
}

// ── Container ────────────────────────────────────────────────────

const FrontSpeakerGhostModel = ({updating}: {updating: boolean}) => {
  const previewMap = useSyncExternalStore(subscribePreviewFrontSpeaker, getPreviewFrontSpeakerSnapshot)
  const removedAllMap = useSyncExternalStore(subscribeRemovedFrontFeature, getRemovedFrontFeatureSnapshot)

  const frontFaceFeatures = useAppSelector((s) => selectActiveScene(s).frontFaceFeatures)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)

  const speakers = useMemo(
    () => frontFaceFeatures.filter((f): f is FrontSpeakerFeature => f.type === 'frontSpeaker'),
    [frontFaceFeatures],
  )

  // Filter removed entries to only front speakers
  const removedSpeakers = useMemo(() => {
    if (!removedAllMap) return []
    return Object.entries(removedAllMap).filter(
      (entry): entry is [string, RemovedFrontSpeakerGhost] => entry[1].type === 'frontSpeaker',
    )
  }, [removedAllMap])

  // Clear removed store when worker finishes
  const prevUpdatingRef = useRef(updating)
  useEffect(() => {
    if (prevUpdatingRef.current && !updating && removedSpeakers.length > 0) {
      clearRemovedFrontFeature()
    }
    prevUpdatingRef.current = updating
  }, [updating, removedSpeakers.length])

  const frontZ = deviceDepth / 2
  const ffExtrude = FRONT_FACE_EXTRUDE
  const ghostZ = frontZ + ffExtrude / 2 + Z_OFFSET

  if (previewMap && !updating) {
    // Only the feature being dragged + removed ghosts
    return (
      <group>
        {Object.entries(previewMap).map(([id, p]) => (
          <SingleFrontSpeakerGhost
            key={id}
            width={p.width}
            height={p.height}
            cornerRadius={p.cornerRadius}
            xOffset={p.xOffset ?? 0}
            yOffset={p.yOffset}
            ghostZ={ghostZ}
          />
        ))}
        {removedSpeakers.map(([id, r]) => (
          <SingleFrontSpeakerGhost
            key={`removed-${id}`}
            width={r.width}
            height={r.height}
            cornerRadius={r.cornerRadius}
            xOffset={r.xOffset ?? 0}
            yOffset={r.yOffset}
            ghostZ={ghostZ}
            color={REMOVED_COLOR}
          />
        ))}
      </group>
    )
  }

  // All features — from Redux with preview overrides
  return (
    <group>
      {speakers.map((sp) => {
        const p = previewMap?.[sp.id]
        return (
          <SingleFrontSpeakerGhost
            key={sp.id}
            width={p?.width ?? sp.width}
            height={p?.height ?? sp.height}
            cornerRadius={p?.cornerRadius ?? sp.cornerRadius}
            xOffset={p?.xOffset ?? sp.xOffset ?? 0}
            yOffset={p?.yOffset ?? sp.yOffset}
            ghostZ={ghostZ}
          />
        )
      })}
      {removedSpeakers.map(([id, r]) => (
        <SingleFrontSpeakerGhost
          key={`removed-${id}`}
          width={r.width}
          height={r.height}
          cornerRadius={r.cornerRadius}
          xOffset={r.xOffset ?? 0}
          yOffset={r.yOffset}
          ghostZ={ghostZ}
          color={REMOVED_COLOR}
        />
      ))}
    </group>
  )
}

export default FrontSpeakerGhostModel
